import { FunctionComponent } from "react";

import { classNames } from "@/utils/classNames";
import { ResolvedLinkedinProspect } from "@/pages/Private/pages/List/schema/list";

import { Checkbox } from "../Checkbox/Checkbox";

interface ResolvedProspectCardProps {
	prospect: ResolvedLinkedinProspect;
	isSelected: boolean;
	failedImages: Set<string>;
	onSelect: (selected: boolean) => void;
	onImageError: (url: string) => void;
}

// Builds "City, State, Country" from whichever location parts are present.
const formatLocation = (prospect: ResolvedLinkedinProspect): string =>
	[prospect.city, prospect.state, prospect.country].filter(Boolean).join(", ");

// Renders a single prospect resolved from a pasted LinkedIn URL. Unlike the name-search
// card, it works off the flat ResolvedLinkedinProspect shape and always renders (the
// person is shown even when no organization resolved).
export const ResolvedProspectCard: FunctionComponent<ResolvedProspectCardProps> = ({
	prospect,
	isSelected,
	failedImages,
	onSelect,
	onImageError,
}) => {
	const { name, title, image, organization } = prospect;
	const location = formatLocation(prospect);
	const displayName = name || prospect.linkedinUrl || "";

	return (
		<div
			className={classNames(
				"w-full border rounded-xl flex items-center cursor-pointer hover:border-gray-400 hover:bg-gray-200/50 p-3 transition-colors gap-3",
				isSelected ? "border-blue-action bg-blue-50" : "border-gray-200"
			)}
			onClick={() => onSelect(!isSelected)}
		>
			<Checkbox className="w-fit!" isChecked={isSelected} />

			{image && !failedImages.has(image) ? (
				<img
					alt={displayName}
					className="w-9 h-9 object-cover rounded-full shrink-0"
					src={image}
					onError={() => onImageError(image)}
				/>
			) : (
				<div className="w-9 h-9 flex items-center justify-center bg-gray-200 text-gray-600 font-semibold rounded-full shrink-0">
					{displayName.charAt(0)?.toUpperCase() || "?"}
				</div>
			)}

			<div className="grow flex flex-col min-w-0">
				<span className="text-sm font-semibold truncate">{displayName}</span>

				{organization?.name && (
					<div className="flex items-center gap-1.5 mt-0.5">
						{organization.logoUrl && !failedImages.has(organization.logoUrl) && (
							<img
								alt={organization.name}
								className="w-4 h-4 rounded object-cover shrink-0"
								src={organization.logoUrl}
								onError={() => onImageError(organization.logoUrl!)}
							/>
						)}
						<span className="text-xs text-gray-500 truncate">{organization.name}</span>
					</div>
				)}

				{title && <span className="text-xs text-gray-600 truncate">{title}</span>}

				{location && <span className="text-xs text-gray-500 truncate">{location}</span>}
			</div>
		</div>
	);
};
